/**
 * White Label PDF Generator
 *
 * A reusable PDF generator that uses White Label letterhead configuration
 * for consistent branded document generation across all modules.
 *
 * Format matches the corporate_letter_with_name.pdf template exactly:
 * - Header: Logo + Brand name | Company name | Tagline | Address | Contact
 * - Footer: 5 lines centered (Company|Address, Contact, License, Page, Confidential)
 *
 * Usage:
 *   import { WhiteLabelPDFGenerator, generateSamplePDF } from './utils/WhiteLabelPDFGenerator';
 *
 *   // Generate a sample/test PDF
 *   await generateSamplePDF(whiteLabelConfig);
 *
 *   // Or use the class for custom documents
 *   const generator = new WhiteLabelPDFGenerator(whiteLabelConfig);
 *   generator.addHeader();
 *   generator.addDate();
 *   generator.addRecipient({...});
 *   generator.addReference('Subject');
 *   generator.addSalutation('Mr. Dupont');
 *   generator.addParagraph('...');
 *   generator.addSignature();
 *   generator.addFooters();
 *   generator.save('document.pdf');
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Converts hex color to RGB array
 * @param {string} hex - Hex color string (e.g., '#0ea5e9')
 * @returns {number[]} RGB array [r, g, b]
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

/**
 * White Label PDF Generator Class
 * Creates professional branded PDFs using White Label letterhead configuration
 * Matches the corporate_letter_with_name.pdf template format
 */
export class WhiteLabelPDFGenerator {
  constructor(config = {}) {
    this.doc = new jsPDF();
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.margin = 20;
    this.yPos = 20;

    // Extract letterhead config with defaults
    // Priority: letterhead-specific fields > main white-label fields > defaults
    this.config = {
      // Brand/Product name (e.g., "HaliTrade")
      brandName: config.brandName || config.companyName || 'HaliTrade',
      poweredBy: config.poweredBy || 'powered by HaliSoft',
      // Legal company name (e.g., "HALISOFT L.L.C-FZ")
      companyName: config.letterheadCompanyName || config.companyName || 'HALISOFT L.L.C-FZ',
      tagline: config.letterheadTagline || config.tagline || 'AI-Powered Trade Finance Solutions',
      registrationNumber: config.letterheadRegistrationNumber || 'XXXXX',
      taxRegistration: config.letterheadTaxRegistration || 'XXXXX',
      signatoryName: config.letterheadSignatoryName || 'Loic Leray',
      signatoryTitle: config.letterheadSignatoryTitle || 'Founder & CEO',
      address: config.letterheadAddress || 'IFZA Business Park, Dubai Silicon Oasis',
      city: config.letterheadCity || 'Dubai',
      country: config.letterheadCountry || 'United Arab Emirates',
      phone: config.letterheadPhone || '+971 XX XXX XXXX',
      email: config.letterheadEmail || config.supportEmail || 'contact@halisoft.com',
      website: config.letterheadWebsite || config.domain || 'www.halisoft.com',
      // Logo: try letterhead logo first, then main logo, then logoUrl
      logoUrl: config.letterheadLogoUrl || config.logoUrl || config.logo || null,
      // Colors
      primaryColor: config.letterheadHeaderGradientStart || config.primaryColor || '#dc2626', // Red for brand
      secondaryColor: config.letterheadHeaderGradientEnd || config.secondaryColor || '#1e40af', // Blue for company
      footerColor: config.letterheadFooterColor || '#374151' // Gray for footer
    };

    // Debug: Log the logo URL being used
    console.log('[WhiteLabelPDFGenerator] Logo URL:', this.config.logoUrl);
    console.log('[WhiteLabelPDFGenerator] Config received:', {
      letterheadLogoUrl: config.letterheadLogoUrl,
      logoUrl: config.logoUrl,
      logo: config.logo
    });

    // Header height (reserved space for header) - white background
    this.headerHeight = 55;
    // Footer height (reserved space for footer)
    this.footerHeight = 28;
    // Content area
    this.contentTop = this.headerHeight + 5;
    this.contentBottom = this.pageHeight - this.footerHeight - 5;

    // Initialize position after header
    this.yPos = this.contentTop;
  }

  /**
   * Add the branded header to the current page
   * Format: Logo + HaliTrade (red) | powered by HaliSoft
   *         HALISOFT L.L.C-FZ (blue, centered)
   *         AI-Powered Trade Finance Solutions (red, italic, centered)
   *         Address | City, Country (gray, centered)
   *         Tel: | Email: | Website (gray, centered)
   */
  addHeader() {
    const doc = this.doc;
    const config = this.config;
    const centerX = this.pageWidth / 2;

    let headerY = 15;
    let logoWidth = 0;

    // Row 1: Logo + Brand Name + "powered by HaliSoft" (left aligned)
    // Try to use the actual logo from white label config
    let hasLogo = false;
    if (config.logoUrl) {
      try {
        // Add the actual logo image (wider to accommodate logo with text)
        doc.addImage(config.logoUrl, 'AUTO', this.margin, headerY - 10, 45, 18);
        logoWidth = 48; // Space after logo
        hasLogo = true;
      } catch (e) {
        console.warn('Could not add logo to PDF:', e);
        // Fallback to placeholder if logo fails
        doc.setFillColor(...hexToRgb(config.primaryColor));
        doc.roundedRect(this.margin, headerY - 8, 12, 12, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('H', this.margin + 4, headerY);
        logoWidth = 16;
      }
    } else {
      // No logo - use placeholder with brand initial
      const brandInitial = config.brandName ? config.brandName.charAt(0).toUpperCase() : 'H';
      doc.setFillColor(...hexToRgb(config.primaryColor));
      doc.roundedRect(this.margin, headerY - 8, 12, 12, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(brandInitial, this.margin + 4, headerY);
      logoWidth = 16;
    }

    const brandColor = hexToRgb(config.primaryColor);

    // "powered by HaliSoft" next to logo
    doc.setTextColor(128, 128, 128); // Gray
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    if (config.poweredBy) {
      doc.text(config.poweredBy, this.margin + logoWidth, headerY);
    }

    // Row 2: Company name centered (blue, bold) - ALWAYS show
    headerY += 12;
    const companyColor = hexToRgb(config.secondaryColor);
    doc.setTextColor(...companyColor);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(config.companyName, centerX, headerY, { align: 'center' });
    headerY += 6;

    // Row 3: Tagline centered (red/primary, italic)
    doc.setTextColor(...brandColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text(config.tagline, centerX, headerY, { align: 'center' });

    // Row 4: Address centered (gray)
    headerY += 5;
    doc.setTextColor(100, 100, 100); // Gray
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const addressLine = `${config.address} | ${config.city}, ${config.country}`;
    doc.text(addressLine, centerX, headerY, { align: 'center' });

    // Row 5: Contact info centered (gray)
    headerY += 4;
    const contactLine = `Tel: ${config.phone} | Email: ${config.email} | ${config.website}`;
    doc.text(contactLine, centerX, headerY, { align: 'center' });

    // Reset text color
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  }

  /**
   * Add date aligned to the right
   * Format: "City, Month Day, Year" (e.g., "Dubai, December 15, 2025")
   */
  addDate() {
    const doc = this.doc;
    const config = this.config;

    const dateStr = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`${config.city}, ${dateStr}`, this.pageWidth - this.margin, this.yPos, { align: 'right' });
    this.yPos += 10;
  }

  /**
   * Add footer to all pages
   * 5 lines centered:
   * 1. Company | Address
   * 2. Tel: | Email: | Web:
   * 3. Trade License: XXXXX | Tax Registration: XXXXX (italic)
   * 4. Page X of Y
   * 5. Confidential - This document contains proprietary information (red, italic)
   */
  addFooters() {
    const doc = this.doc;
    const config = this.config;
    const pageCount = doc.internal.getNumberOfPages();
    const centerX = this.pageWidth / 2;
    const footerColor = hexToRgb(config.footerColor);

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      let lineY = this.pageHeight - this.footerHeight + 3;

      // Line 1: Company | Address (gray)
      doc.setTextColor(...footerColor);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      const line1 = `${config.companyName} | ${config.address}, ${config.city}, UAE`;
      doc.text(line1, centerX, lineY, { align: 'center' });

      // Line 2: Tel | Email | Web (gray)
      lineY += 3.5;
      const line2 = `Tel: ${config.phone} | Email: ${config.email} | Web: ${config.website}`;
      doc.text(line2, centerX, lineY, { align: 'center' });

      // Line 3: Trade License | Tax Registration (gray, italic)
      lineY += 3.5;
      doc.setFont('helvetica', 'italic');
      const line3 = `Trade License: ${config.registrationNumber} | Tax Registration: ${config.taxRegistration}`;
      doc.text(line3, centerX, lineY, { align: 'center' });

      // Line 4: Page X of Y (gray)
      lineY += 3.5;
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, centerX, lineY, { align: 'center' });

      // Line 5: Confidential (red, italic)
      lineY += 3.5;
      doc.setTextColor(220, 38, 38); // Red
      doc.setFont('helvetica', 'italic');
      doc.text('Confidential - This document contains proprietary information', centerX, lineY, { align: 'center' });

      // Reset
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
    }
  }

  /**
   * Check if we need a new page and add one if needed
   * @param {number} requiredSpace - Space needed for next content
   * @returns {boolean} - True if new page was added
   */
  checkPageBreak(requiredSpace = 20) {
    if (this.yPos + requiredSpace > this.contentBottom) {
      this.doc.addPage();
      this.addHeader();
      this.yPos = this.contentTop;
      return true;
    }
    return false;
  }

  /**
   * Add recipient information (To: section)
   * @param {Object} recipient - Recipient details
   */
  addRecipient(recipient = {}) {
    this.checkPageBreak(35);

    this.doc.setFontSize(10);
    this.doc.setTextColor(0, 0, 0);

    if (recipient.name) {
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(recipient.name, this.margin, this.yPos);
      this.yPos += 5;
    }
    if (recipient.title) {
      this.doc.text(recipient.title, this.margin, this.yPos);
      this.yPos += 5;
    }
    if (recipient.company) {
      this.doc.text(recipient.company, this.margin, this.yPos);
      this.yPos += 5;
    }
    if (recipient.address) {
      this.doc.text(recipient.address, this.margin, this.yPos);
      this.yPos += 5;
    }
    if (recipient.city) {
      this.doc.text(recipient.city, this.margin, this.yPos);
      this.yPos += 5;
    }

    this.yPos += 5;
  }

  /**
   * Add a reference line (Re: subject) - bold
   * @param {string} subject - Reference subject
   */
  addReference(subject) {
    this.checkPageBreak(12);

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(0, 0, 0);
    this.doc.text(`Re: ${subject}`, this.margin, this.yPos);
    this.yPos += 8;

    this.doc.setFont('helvetica', 'normal');
  }

  /**
   * Add a salutation
   * @param {string} name - Recipient name (e.g., "Mr. Dupont")
   */
  addSalutation(name = '') {
    this.checkPageBreak(10);

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(0, 0, 0);
    this.doc.text(`Dear ${name || 'Sir/Madam'},`, this.margin, this.yPos);
    this.yPos += 8;
  }

  /**
   * Add a paragraph of text
   * @param {string} text - Paragraph text
   */
  addParagraph(text) {
    this.checkPageBreak(15);

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(0, 0, 0);

    const lines = this.doc.splitTextToSize(text, this.pageWidth - (this.margin * 2));
    this.doc.text(lines, this.margin, this.yPos);
    this.yPos += (lines.length * 4.5) + 5;
  }

  /**
   * Add a section header
   * @param {string} text - Section header text
   */
  addSectionHeader(text) {
    this.checkPageBreak(15);

    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(31, 41, 55);
    this.doc.text(text, this.margin, this.yPos);
    this.yPos += 7;

    this.doc.setTextColor(0, 0, 0);
    this.doc.setFont('helvetica', 'normal');
  }

  /**
   * Add a bullet list
   * @param {string[]} items - Array of bullet items
   */
  addBulletList(items) {
    this.checkPageBreak(items.length * 6 + 5);

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');

    items.forEach(item => {
      const lines = this.doc.splitTextToSize(`• ${item}`, this.pageWidth - (this.margin * 2) - 5);
      this.doc.text(lines, this.margin + 5, this.yPos);
      this.yPos += (lines.length * 4.5) + 2;
    });

    this.yPos += 3;
  }

  /**
   * Add a table
   * @param {string[]} headers - Table headers
   * @param {Array<Array>} data - Table data rows
   * @param {Object} options - Table options
   */
  addTable(headers, data, options = {}) {
    this.checkPageBreak(40);

    const headerColor = hexToRgb(this.config.secondaryColor); // Blue

    autoTable(this.doc, {
      startY: this.yPos,
      head: [headers],
      body: data,
      theme: options.theme || 'grid',
      headStyles: {
        fillColor: headerColor,
        fontSize: 9,
        fontStyle: 'bold',
        textColor: [255, 255, 255]
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      margin: { left: this.margin, right: this.margin },
      ...options
    });

    this.yPos = this.doc.lastAutoTable.finalY + 10;
  }

  /**
   * Add closing text (e.g., "We look forward to hearing from you...")
   * @param {string} text - Closing paragraph
   */
  addClosing(text) {
    this.addParagraph(text);
  }

  /**
   * Add signature section
   * Format:
   *   Yours sincerely,
   *   [space for signature]
   *   Name
   *   **Title** (bold)
   *   Company
   * @param {string} closingText - Closing text (default: "Yours sincerely,")
   */
  addSignature(closingText = 'Yours sincerely,') {
    this.checkPageBreak(35);

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(0, 0, 0);
    this.doc.text(closingText, this.margin, this.yPos);
    this.yPos += 12; // Space for signature

    // Signatory name (normal)
    const signatoryName = this.config.signatoryName || 'Loic Leray';
    this.doc.text(signatoryName, this.margin, this.yPos);
    this.yPos += 5;

    // Signatory title (bold)
    const signatoryTitle = this.config.signatoryTitle || 'Founder & CEO';
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(signatoryTitle, this.margin, this.yPos);
    this.yPos += 5;

    // Company name (normal)
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(this.config.companyName, this.margin, this.yPos);
    this.yPos += 10;
  }

  /**
   * Add vertical space
   * @param {number} space - Space in mm
   */
  addSpace(space = 5) {
    this.yPos += space;
    this.checkPageBreak(0);
  }

  /**
   * Add a document title (centered, blue)
   * @param {string} title - Document title
   * @param {string} subtitle - Optional subtitle
   */
  addTitle(title, subtitle = '') {
    this.checkPageBreak(25);

    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(30, 64, 175); // Blue
    this.doc.text(title, this.pageWidth / 2, this.yPos, { align: 'center' });
    this.yPos += 7;

    if (subtitle) {
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(100, 100, 100);
      this.doc.text(subtitle, this.pageWidth / 2, this.yPos, { align: 'center' });
      this.yPos += 5;
    }

    // Decorative line
    this.doc.setDrawColor(200, 200, 200);
    this.doc.setLineWidth(0.3);
    this.doc.line(this.margin + 20, this.yPos, this.pageWidth - this.margin - 20, this.yPos);
    this.yPos += 10;

    this.doc.setTextColor(0, 0, 0);
  }

  /**
   * Save the PDF
   * @param {string} filename - Output filename
   */
  save(filename = 'document.pdf') {
    this.addFooters();
    this.doc.save(filename);
    return { success: true, filename };
  }

  /**
   * Get the PDF as blob
   * @returns {Blob}
   */
  getBlob() {
    this.addFooters();
    return this.doc.output('blob');
  }

  /**
   * Get the jsPDF document instance for advanced operations
   * @returns {jsPDF}
   */
  getDoc() {
    return this.doc;
  }
}

/**
 * Generate a sample/test PDF to verify letterhead configuration
 * Matches the corporate_letter_with_name.pdf template exactly
 * @param {Object} config - White Label configuration
 * @returns {Promise<{success: boolean, filename: string}>}
 */
export const generateSamplePDF = async (config) => {
  const generator = new WhiteLabelPDFGenerator(config);

  // Extract brand names for dynamic content
  const brandName = config.brandName || config.companyName || 'HaliTrade';
  const companyName = config.letterheadCompanyName || config.companyName || 'HaliSoft';
  // Extract "powered by X" or use company name
  const poweredByName = config.poweredBy ? config.poweredBy.replace('powered by ', '') : companyName;

  // Add header to first page
  generator.addHeader();

  // Date aligned right
  generator.addDate();

  // Recipient
  generator.addRecipient({
    name: 'Mr. Jean Dupont',
    title: 'Chief Executive Officer',
    company: 'ABC Trading Company',
    address: '123 Business Avenue',
    city: 'Paris, France'
  });

  // Reference (bold)
  generator.addReference('Invoice Factoring Services Proposal');

  // Salutation
  generator.addSalutation('Mr. Dupont');

  // Content paragraphs - USE DYNAMIC BRAND NAMES
  generator.addParagraph(
    `We are pleased to introduce ${brandName}, powered by ${poweredByName}, your innovative partner in invoice financing. Our revolutionary technology platform enables your business to transform receivables into immediate liquidity, optimizing your cash flow and accelerating your growth.`
  );

  generator.addParagraph(
    'Through our AI-powered system, we reduce approval times from 3-5 weeks to just 24-72 hours. Our fully digitalized onboarding process ensures a smooth and professional customer experience while maintaining the highest standards of security and compliance.'
  );

  generator.addParagraph(
    `Our solutions include invoice financing, reverse factoring, and flexible credit lines tailored to your specific needs. We would be delighted to schedule a meeting to discuss how ${brandName} can support your business development.`
  );

  generator.addParagraph(
    'We look forward to hearing from you and remain at your disposal for any further information.'
  );

  // Signature
  generator.addSignature();

  // Save
  const filename = `${companyName.replace(/\s+/g, '_')}_Sample_Letter_${new Date().toISOString().split('T')[0]}.pdf`;

  return generator.save(filename);
};

/**
 * Generate a termsheet PDF using White Label branding
 * @param {Object} config - White Label configuration
 * @param {Object} termsheetData - Termsheet data
 * @returns {Promise<{success: boolean, filename: string}>}
 */
export const generateTermsheetPDF = async (config, termsheetData) => {
  const generator = new WhiteLabelPDFGenerator(config);

  generator.addHeader();
  generator.addDate();
  generator.addTitle('TERM SHEET', termsheetData.reference || '');

  // Parties section
  generator.addSectionHeader('PARTIES');
  generator.addTable(
    ['Role', 'Party'],
    [
      ['Factor', config.letterheadCompanyName || config.companyName || 'Factor'],
      ['Supplier', termsheetData.supplier || '-'],
      ['Buyer', termsheetData.buyer || '-']
    ]
  );

  // Terms section
  generator.addSectionHeader('TERMS & CONDITIONS');
  generator.addTable(
    ['Parameter', 'Value'],
    [
      ['Facility Limit', termsheetData.facilityLimit || '-'],
      ['Advance Rate', termsheetData.advanceRate || '-'],
      ['Discount Rate', termsheetData.discountRate || '-'],
      ['Tenor', termsheetData.tenor || '-'],
      ['Currency', termsheetData.currency || 'USD']
    ]
  );

  // Signature
  generator.addSpace(20);
  generator.addSignature('For and on behalf of the Factor:');

  const filename = `Termsheet_${termsheetData.reference || 'Draft'}_${new Date().toISOString().split('T')[0]}.pdf`;
  return generator.save(filename);
};

export default WhiteLabelPDFGenerator;
